import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Avatar,
  IconButton,
  Typography,
  TextField,
  Paper,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CallIcon from "@mui/icons-material/Call";
import VideocamIcon from "@mui/icons-material/Videocam";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import SendIcon from "@mui/icons-material/Send";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

import { useDispatch, useSelector } from "react-redux";
import {
  createOrGetThread,
  sendMessage,
  listenToMessages,
  upsertLastMessage,
} from "../../redux/threadsSlice";
import { fetchUserData, listenForAuthChanges } from "../../redux/authSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const Messages: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { user, allUsers, supportId } = useSelector((s: RootState) => s.auth);
  const { messages } = useSelector((s: RootState) => s.threads);

  const [threadId, setThreadId] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [showWarning, setShowWarning] = useState(false);

  const flatListRef = useRef<HTMLDivElement>(null);

  const { userId: paramId } = useParams<{ userId: string }>();
  const location = useLocation();

  const recipientId =
    paramId ||
    (location.state as { userIdB?: string })?.userIdB ||
    supportId ||
    "";

  const currentUserId = user?.userId || "";
  const recipient = allUsers?.find((u) => u.userId === recipientId);

  useEffect(() => {
    if (!user) dispatch(listenForAuthChanges());
  }, [dispatch, user]);

  useEffect(() => {
    if (user?.userId) dispatch(fetchUserData());
  }, [dispatch, user?.userId]);

  useEffect(() => {
    if (!recipientId || !currentUserId) return;

    dispatch(createOrGetThread({ userIdB: recipientId }))
      .unwrap()
      .then((thread) => {
        setThreadId(thread.id);
        dispatch(listenToMessages({ threadId: thread.id }));
      });
  }, [dispatch, recipientId, currentUserId]);

  const threadMessages = threadId ? messages[threadId] || [] : [];

  const violatesContactRules = (input: string): boolean => {
    if (!input) return false;
    if (/[0-9]/.test(input)) return true;
    if (/[+@#%^*&()/\\\-_=]/.test(input)) return true;
    if (input.toLowerCase().includes(".com")) return true;
    return false;
  };

  const handleSend = () => {
    if (!text.trim() || !threadId || !currentUserId) return;

    if (violatesContactRules(text)) {
      setShowWarning(true);
      return;
    }



    const newMessage = { id: Date.now().toString(), senderId: currentUserId, text, type: "text", timestamp: Date.now(), status: {}, }; dispatch(sendMessage({ threadId, text })); dispatch(upsertLastMessage({ threadId, message: newMessage })); setText(""); };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return "";
    const date =
      typeof timestamp === "number"
        ? new Date(timestamp)
        : new Date(timestamp.seconds * 1000);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "";
    const date =
      typeof timestamp === "number"
        ? new Date(timestamp)
        : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const groupedMessages: { [date: string]: any[] } = {};
  threadMessages.forEach((msg) => {
    const dateKey = formatDate(msg.timestamp);
    if (!groupedMessages[dateKey]) groupedMessages[dateKey] = [];
    groupedMessages[dateKey].push(msg);
  });

  useEffect(() => {
    flatListRef.current?.scrollTo({
      top: flatListRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [threadMessages]);

  return (
    <Box
      sx={{
        height: "100vh",
        background: isMobile ? "#ffffff" : "#f3f3f3",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: isMobile ? 0 : 2,
      }}
    >
      {/* Chat Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: isMobile ? "100%" : 480,
          height: "100%",
          borderRadius: isMobile ? 0 : "24px",
          background: "#ffffff",
          boxShadow: isMobile
            ? "none"
            : "0 25px 60px rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <Box
          display="flex"
          alignItems="center"
          px={2}
          py={1.5}
          borderBottom="1px solid #eee"
        >
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon sx={{ color: "#000" }} />
          </IconButton>

          {recipient?.profilePicture ? (
            <Avatar src={recipient.profilePicture} sx={{ mx: 1 }} />
          ) : (
            <Avatar sx={{ bgcolor: "#f0f0f0", mx: 1 }}>
              <PersonOutlineIcon sx={{ color: "#000" }} />
            </Avatar>
          )}

          <Typography fontWeight={600} flexGrow={1} color="#000">
            {recipient?.firstName || "User"}
          </Typography>

        {/*
          <IconButton>
            <CallIcon sx={{ color: "#000" }} />
          </IconButton>
          <IconButton>
            <VideocamIcon sx={{ color: "#000" }} />
          </IconButton>
          <IconButton>
            <HeadsetMicIcon sx={{ color: "#000" }} />
          </IconButton>
        
        */}
        </Box>

        {/* MESSAGES */}
        <Box ref={flatListRef} flexGrow={1} overflow="auto" p={2}>
          {Object.keys(groupedMessages).map((dateKey) => (
            <Box key={dateKey}>
              <Box display="flex" justifyContent="center" my={2}>
                <Typography variant="caption" color="#888">
                  {dateKey}
                </Typography>
              </Box>

              {groupedMessages[dateKey].map((msg) => {
                const isMe = msg.senderId === currentUserId;

                return (
                  <Box
                    key={msg.id}
                    display="flex"
                    justifyContent={isMe ? "flex-end" : "flex-start"}
                    my={0.5}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        maxWidth: "70%",
                        p: 1.5,
                        borderRadius: 3,
                        backgroundColor: isMe ? "#000" : "#f5f5f5",
                        color: isMe ? "#fff" : "#000",
                      }}
                    >
                      <Typography variant="body1">
                        {msg.text}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          opacity: 0.6,
                          textAlign: "right",
                        }}
                      >
                        {formatTime(msg.timestamp)}
                      </Typography>
                    </Paper>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>

        <Divider />

        {/* INPUT */}
        <Box display="flex" alignItems="center" p={1.5}>
          <TextField
            fullWidth
            size="small"
            placeholder="Type a message..."
            value={text}
            onChange={(e) => {
              const value = e.target.value;
              if (violatesContactRules(value)) {
                setShowWarning(true);
                return;
              }
              setText(value);
            }}
            sx={{
              mr: 1,
              backgroundColor: "#f5f5f5",
              borderRadius: "20px",
              "& fieldset": { border: "none" },
            }}
          />

          <IconButton
            onClick={handleSend}
            sx={{
              backgroundColor: "#000",
              color: "#fff",
              "&:hover": { backgroundColor: "#222" },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default Messages;